<script setup>
import CaretDownIcon from '@iconify-vue/ph/caret-down';
import EyeIcon from '@iconify-vue/ph/eye';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import LinkCell from '@js/components/tables/cells/LinkCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { hasPositionTracking } = defineProps({
    hasPositionTracking: {
        type: Boolean,
        required: false,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    filters,
    availableFilters,
    setGlobalFilter,
    setFilters,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'name',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Nom',
                }),
            cell: (props) =>
                h(LinkCell, {
                    text: props.row.original.name,
                    href: route(
                        'front.projectManagement.seo.positionTracking',
                        {
                            tenant: props.row.original.id,
                        },
                    ),
                    inertia: true,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'lastUpdatedAt',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Dernière mise à jour',
                }),
            cell: (props) =>
                props.row.original.lastUpdatedAt
                    ? h(TextCell, {
                          text: props.row.original.lastUpdatedAt,
                      })
                    : undefined,
            enableSorting: false,
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (innerProps) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            !innerProps.row.getParentRow()
                                ? h(ActionButton, {
                                      size: 'small',
                                      square: true,
                                      title: 'Voir',
                                      href: route(
                                          'front.projectManagement.seo.positionTracking',
                                          {
                                              tenant: innerProps.row.original
                                                  .id,
                                          },
                                      ),
                                      icon: EyeIcon,
                                  })
                                : null,
                            innerProps.row.getCanExpand()
                                ? h(ActionButton, {
                                      size: 'small',
                                      type: 'secondary',
                                      square: true,
                                      title: innerProps.row.getIsExpanded()
                                          ? 'Réduire'
                                          : 'Développer',
                                      callback: () =>
                                          innerProps.row.toggleExpanded(),
                                      icon: CaretDownIcon,
                                      rotateIcon:
                                          innerProps.row.getIsExpanded(),
                                  })
                                : null,
                        ],
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.seo.postPositionTracking'),
    data: {
        hasPositionTracking,
    },
    columnPinning: {
        right: ['actions'],
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :filters="filters"
        :available-filters="availableFilters"
        :set-filters="setFilters"
        :set-global-filter="setGlobalFilter"
        :loading="loading"
    />
</template>
